import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gradeService, Grade, GradeHorario, periodoService, Periodo, ConfiguracaoGrade, configuracaoGradeService, turmaService, apiServices, agendaTurmaService, agendaTurmaHorarioService, professorAgendaService, agendaProfessorService } from '../services/api';
import LoadingModal from './LoadingModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import CargaHorariaValidationModal from './CargaHorariaValidationModal';

const Grades: React.FC = () => {
  const navigate = useNavigate();
  const [grades, setGrades] = useState<Grade[]>([]);
  const [periodos, setPeriodos] = useState<Periodo[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingPeriodos, setLoadingPeriodos] = useState(false);
  const [generatingGrade, setGeneratingGrade] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateModalState, setGenerateModalState] = useState<'loading' | 'success' | 'error'>('loading');
  const [generateModalTitle, setGenerateModalTitle] = useState('Gerando Grade');
  const [generateModalMessage, setGenerateModalMessage] = useState('Aguarde enquanto a grade está sendo gerada...');
  const [generateModalSugestoes, setGenerateModalSugestoes] = useState<string[]>([]);
  const [generatedGradeId, setGeneratedGradeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [turmasComErro, setTurmasComErro] = useState<{nome: string; cargaHorariaTotal: number; cargaHorariaAgenda: number; agendaNome: string}[]>([]);
  const [professoresComErro, setProfessoresComErro] = useState<{nome: string; horariosLivres: number; aulasNecessarias: number}[]>([]);
  const [cargaHorariaPeriodo, setCargaHorariaPeriodo] = useState(0);

  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [generateFormData, setGenerateFormData] = useState({
    nome: '',
    periodoId: ''
  });
  const [configuracao, setConfiguracao] = useState<ConfiguracaoGrade>({
    id: '',
    aulaGeminada: 2,
    aulaGeminadaObrigatoria: false,
    quantidadeAulasPeriodo: 2
  });
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    itemName: '',
    onConfirm: () => {},
    isLoading: false
  });

  useEffect(() => {
    loadData();
    loadPeriodos();
    loadConfiguracao();
  }, []);

  // Cleanup do polling quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Sem dependências para evitar loop infinito

  const loadConfiguracao = async () => {
    try {
      setLoadingConfig(true);
      const data = await configuracaoGradeService.buscar();
      setConfiguracao(data);
    } catch (error: any) {
      console.error('Erro ao carregar configuração:', error);
      // Se o endpoint retornar 405, usar valores padrão
      if (error?.response?.status === 405) {
        setConfiguracao({
          id: '1',
          aulaGeminada: 2,
          aulaGeminadaObrigatoria: false,
          quantidadeAulasPeriodo: 2
        });
      }
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleConfigInputChange = async (field: keyof ConfiguracaoGrade, value: any) => {
    const novaConfiguracao = {
      ...configuracao,
      [field]: value
    };
    
    setConfiguracao(novaConfiguracao);
    
    // Atualizar configurações automaticamente na API
    try {
      await configuracaoGradeService.atualizar(novaConfiguracao);
    } catch (error: any) {
      console.warn('Não foi possível salvar as configurações automaticamente:', error);
      // Continua mesmo se não conseguir salvar as configurações
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await gradeService.getAll();
      setGrades(data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar grades');
      console.error('Erro ao carregar grades:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPeriodos = async () => {
    try {
      setLoadingPeriodos(true);
      const periodosData = await periodoService.getAll();
      setPeriodos(periodosData);
    } catch (err) {
      console.error('Erro ao carregar períodos:', err);
    } finally {
      setLoadingPeriodos(false);
    }
  };



  const validateCargaHoraria = async (periodoId: string): Promise<boolean> => {
    try {
      // Buscar o período selecionado
      const periodo = periodos.find(p => p.id === periodoId);
      if (!periodo) {
        setError('Período não encontrado.');
        return false;
      }

      // Buscar todas as turmas e filtrar pelo período
      const todasTurmas = await turmaService.getAll();
      const turmas = todasTurmas.filter(turma => turma.periodo?.id === periodoId);
      
      const turmasComProblema: {nome: string; cargaHorariaTotal: number; cargaHorariaAgenda: number; agendaNome: string}[] = [];
      
      // Verificar cada turma
      for (const turma of turmas) {
        if (!turma.id) continue;
        
        // Buscar disciplinas da turma
        const disciplinas = await apiServices.getDisciplinasByTurma(turma.id);
        
        // Calcular carga horária total da turma
        const cargaHorariaTotal = disciplinas.reduce((total: number, disciplina: any) => {
          return total + (disciplina.cargaHoraria || 0);
        }, 0);
        
        // Buscar agenda da turma e calcular horários livres
        const agendas = await agendaTurmaService.getByTurma(turma.id);
        
        // Para cada agenda da turma, verificar se há inconsistência
        for (const agenda of agendas) {
          const cargaHorariaAgenda = await calcularCargaHorariaAgenda(agenda.id!);
          
          if (cargaHorariaTotal !== cargaHorariaAgenda) {
            turmasComProblema.push({
              nome: turma.nome,
              cargaHorariaTotal,
              cargaHorariaAgenda,
              agendaNome: agenda.nome
            });
          }
        }
        
        // Se a turma não tem agenda, usar validação do período como fallback
        if (agendas.length === 0) {
          const cargaHorariaPeriodoCalculada = await calcularCargaHorariaPeriodo(periodoId);
          
          if (cargaHorariaTotal !== cargaHorariaPeriodoCalculada) {
            turmasComProblema.push({
              nome: turma.nome,
              cargaHorariaTotal,
              cargaHorariaAgenda: cargaHorariaPeriodoCalculada,
              agendaNome: 'Período (sem agenda específica)'
            });
          }
        }
      }
      
      if (turmasComProblema.length > 0) {
        setTurmasComErro(turmasComProblema);
        setCargaHorariaPeriodo(await calcularCargaHorariaPeriodo(periodoId));
        setIsValidationModalOpen(true);
        return false;
      }
      
      return true;
    } catch (error: any) {
      console.error('Erro ao validar carga horária:', error);
      setError('Erro ao validar carga horária das turmas.');
      return false;
    }
  };

  const calcularCargaHorariaPeriodo = async (periodoId: string): Promise<number> => {
    try {
      // Buscar horários do período para calcular a carga horária total
      const horarios = await apiServices.getHorariosByPeriodo(periodoId);
      return horarios.length; // Cada horário representa 1 aula
    } catch (error) {
      console.error('Erro ao calcular carga horária do período:', error);
      return 0;
    }
  };

  const calcularCargaHorariaAgenda = async (agendaId: string): Promise<number> => {
    try {
      // Buscar horários da agenda da turma
      const horariosAgenda = await agendaTurmaHorarioService.getByAgenda(agendaId);
      
      // Contar apenas os horários disponíveis (livres)
      const horariosLivres = horariosAgenda.filter(horario => horario.disponivel);
      
      return horariosLivres.length; // Cada horário livre representa 1 aula disponível
    } catch (error) {
      console.error('Erro ao calcular carga horária da agenda:', error);
      return 0;
    }
  };

  // Função para validar agenda dos professores antes de gerar grade
  const validateAgendaProfessores = async (periodoId: string): Promise<{isValid: boolean; professoresComErro: any[]}> => {
    try {
      // Buscar todas as turmas do período
      const todasTurmas = await turmaService.getAll();
      const turmasDoPeriodo = todasTurmas.filter(turma => turma.periodo?.id === periodoId);
      
      const professoresComProblema: {nome: string; horariosLivres: number; aulasNecessarias: number}[] = [];
      
      // Agrupar professores e suas disciplinas
      const professoresMap = new Map<string, {nome: string; professorId: number; aulasTotal: number}>();
      
      for (const turma of turmasDoPeriodo) {
        if (!turma.id) continue;

        // Buscar disciplinas da turma
        const disciplinas = await apiServices.getDisciplinasByTurma(turma.id);
        
        disciplinas.forEach((disciplina: any) => {
          if (disciplina.professor?.nome && disciplina.professor?.id && disciplina.cargaHoraria) {
            const professorNome = disciplina.professor.nome;
            const professorId = disciplina.professor.id;
            const aulasAtual = professoresMap.get(professorNome)?.aulasTotal || 0;
            professoresMap.set(professorNome, {
              nome: professorNome,
              professorId: professorId,
              aulasTotal: aulasAtual + disciplina.cargaHoraria
            });
          }
        });
      }
      
      // Verificar agenda de cada professor
      for (const [professorNome, dadosProfessor] of Array.from(professoresMap)) {
        try {
          // Buscar agendas do professor usando a API correta
          const agendasProfessor = await professorAgendaService.getAgendasByProfessor(dadosProfessor.professorId);
          const agendaDoPeriodo = agendasProfessor.find((agenda: any) => agenda.periodo?.id === periodoId);
          
          if (!agendaDoPeriodo?.id) {
            // Professor sem agenda para o período - considerar como problema
            professoresComProblema.push({
              nome: professorNome,
              horariosLivres: 0,
              aulasNecessarias: dadosProfessor.aulasTotal
            });
            continue;
          }
          
          // Buscar horários disponíveis na agenda do professor usando a API correta
          const horariosAgenda = await agendaProfessorService.getByAgenda(agendaDoPeriodo.id.toString());
          const horariosLivres = horariosAgenda.filter(h => h.disponivel).length;
          
          // Verificar se professor tem horários suficientes
          if (horariosLivres < dadosProfessor.aulasTotal) {
            professoresComProblema.push({
              nome: professorNome,
              horariosLivres,
              aulasNecessarias: dadosProfessor.aulasTotal
            });
          }
        } catch (error) {
          console.error(`Erro ao verificar agenda do professor ${professorNome}:`, error);
          // Em caso de erro, considerar como problema
          professoresComProblema.push({
            nome: professorNome,
            horariosLivres: 0,
            aulasNecessarias: dadosProfessor.aulasTotal
          });
        }
      }
      
      return {
        isValid: professoresComProblema.length === 0,
        professoresComErro: professoresComProblema
      };
    } catch (error) {
      console.error('Erro ao validar agenda dos professores:', error);
      return {
        isValid: false,
        professoresComErro: []
      };
    }
  };

  // Função para iniciar o polling de verificação de status
  const startPolling = (taskId: string) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    const checkStatus = async () => {
      try {
        const response = await gradeService.verificarStatus(taskId);

        if (response.status === 'Concluído') {
          await loadData();
          stopPolling(interval);

          setGenerateModalState('success');
          setGeneratedGradeId(response.gradeId ?? null);
          const sugestoes = (response.diagnostico ?? []).map((item) => item.mensagem);
          setGenerateModalSugestoes(sugestoes);
          setGenerateModalTitle(sugestoes.length > 0 ? 'Grade gerada com avisos' : 'Grade gerada com sucesso!');
          setGenerateModalMessage(
            sugestoes.length > 0
              ? (response.message ?? 'Algumas preferências não puderam ser totalmente atendidas. Veja as sugestões abaixo para deixar a grade perfeita:')
              : (response.message ?? 'A grade foi gerada sem nenhum conflito.')
          );
          setSuccess('Grade gerada com sucesso!');
        } else if (response.status === 'Em processamento' && response.message) {
          setGenerateModalMessage(response.message);
        }
      } catch (error: any) {
        const dadosFalha = error?.response?.status === 409 ? error.response.data : null;

        if (dadosFalha?.status === 'Falhou') {
          stopPolling(interval);
          setGenerateModalState('error');
          setGenerateModalTitle('Não foi possível gerar a grade');
          setGenerateModalMessage(dadosFalha.message || 'Ocorreu um erro ao gerar a grade.');
        } else {
          console.error('Erro ao verificar status da geração de grade:', error);
        }
      }
    };

    checkStatus();

    const interval = setInterval(checkStatus, 2000);

    setPollingInterval(interval);
  };

  // Função para parar o polling
  const stopPolling = (intervalIdToClear?: NodeJS.Timeout | null) => {
    const intervalToClear = intervalIdToClear || pollingInterval;

    if (intervalToClear) {
      clearInterval(intervalToClear);
      setPollingInterval(null);
    }
  };

  const executarGeracao = async () => {
    try {
      setGeneratingGrade(true);
      setShowGenerateModal(true);
      setGenerateModalState('loading');
      setGenerateModalTitle('Gerando Grade');
      setGenerateModalMessage('Enviando solicitação ao servidor...');
      setGenerateModalSugestoes([]);
      setGeneratedGradeId(null);
      setError(null);

      // Salvar configurações antes de gerar a grade
      try {
        await configuracaoGradeService.atualizar(configuracao);
      } catch (configError: any) {
        console.warn('Não foi possível salvar as configurações:', configError);
        // Continua mesmo se não conseguir salvar as configurações
      }

      // Encontrar o período selecionado para concatenar o nome
      const periodoSelecionado = periodos.find(p => p.id === generateFormData.periodoId);
      const nomeComPeriodo = periodoSelecionado
        ? `${generateFormData.nome} - Período: ${periodoSelecionado.nome}`
        : generateFormData.nome;

      const gradeRequest = {
        nome: nomeComPeriodo
      };

      const response = await gradeService.gerar(generateFormData.periodoId, gradeRequest);

      if (!response.taskId) {
        throw new Error('TaskId não retornado pela API');
      }

      setGenerateModalMessage('Solicitação aceita. Acompanhando o processamento...');
      startPolling(response.taskId);

      setShowGenerateForm(false);
      setGenerateFormData({ nome: '', periodoId: '' });
    } catch (err: any) {
      setGenerateModalState('error');
      setGenerateModalTitle('Falha na Geração da Grade');

      let errorMessage = 'Erro ao gerar grade. Verifique se há disciplinas e professores cadastrados para o período selecionado.';

      if (err?.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setGenerateModalMessage(errorMessage);
      setError(errorMessage);
      console.error('Erro ao gerar grade:', err);
    } finally {
      setGeneratingGrade(false);
    }
  };

  const handleGenerateGrade = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!generateFormData.periodoId || !generateFormData.nome) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    setError(null);

    // Validações são só um aviso prévio (mais rápido que esperar o solver) — não
    // bloqueiam mais a geração, o motor sabe lidar com isso via diagnóstico.
    const isValid = await validateCargaHoraria(generateFormData.periodoId);
    if (!isValid) {
      return;
    }

    const validacaoAgenda = await validateAgendaProfessores(generateFormData.periodoId);
    if (!validacaoAgenda.isValid) {
      setTurmasComErro([]);
      setProfessoresComErro(validacaoAgenda.professoresComErro);
      setCargaHorariaPeriodo(await calcularCargaHorariaPeriodo(generateFormData.periodoId));
      setIsValidationModalOpen(true);
      return;
    }

    await executarGeracao();
  };

  const handleViewGrade = (grade: Grade) => {
    if (grade.id) {
      navigate(`/grades/view/${grade.id}`);
    }
  };



  const handleDelete = (id: string) => {
    const grade = grades.find(g => g.id === id);
    setDeleteModal({
      isOpen: true,
      title: 'Excluir Grade',
      message: 'Tem certeza que deseja excluir esta grade?',
      itemName: grade?.nome || '',
      onConfirm: () => confirmDeleteGrade(id),
      isLoading: false
    });
  };

  const confirmDeleteGrade = async (id: string) => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    try {
      await gradeService.delete(id);
      setSuccess('Grade excluída com sucesso!');
      loadData();
      setError(null);
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
    } catch (err) {
      setError('Erro ao excluir grade');
      console.error('Erro ao excluir grade:', err);
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };



  const filteredGrades = grades
    .filter(grade => grade.nome.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      // Ordenar da mais nova para mais antiga
      if (!a.data && !b.data) return 0;
      if (!a.data) return 1;
      if (!b.data) return -1;
      return new Date(b.data).getTime() - new Date(a.data).getTime();
    });

  // Funções utilitárias para organizar a grade
  const diasSemana = [
    { numero: 1, nome: 'Segunda-feira' },
    { numero: 2, nome: 'Terça-feira' },
    { numero: 3, nome: 'Quarta-feira' },
    { numero: 4, nome: 'Quinta-feira' },
    { numero: 5, nome: 'Sexta-feira' },
    { numero: 6, nome: 'Sábado' },
    { numero: 7, nome: 'Domingo' }
  ];

  const organizarGradePorDia = (horarios: GradeHorario[]) => {
    const gradePorDia: { [key: number]: GradeHorario[] } = {};
    
    diasSemana.forEach(dia => {
      gradePorDia[dia.numero] = horarios.filter(h => h.horario.diaSemana === dia.numero);
    });
    
    return gradePorDia;
  };

  const obterHorariosUnicos = (horarios: GradeHorario[]) => {
    const horariosUnicos = new Set<string>();
    horarios.forEach(h => {
      horariosUnicos.add(`${h.horario.inicio}-${h.horario.fim}`);
    });
    return Array.from(horariosUnicos).sort();
  };

  const obterTurmasUnicas = (horarios: GradeHorario[]) => {
    const turmasUnicas = new Map<string, string>();
    horarios.forEach(h => {
      turmasUnicas.set(h.turma.id, h.turma.nome);
    });
    return Array.from(turmasUnicas.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  };

  const findHorarioForTurmaAndTime = (horarios: GradeHorario[], turmaId: string, inicio: string, fim: string) => {
    return horarios.find(h => 
      h.turma.id === turmaId && 
      h.horario.inicio === inicio && 
      h.horario.fim === fim
    );
  };

  return (
    <>
    <div className="main-content">
      <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="fas fa-table me-2"></i>
              Gerenciar Grades
            </h2>
            <div className="d-flex gap-2">
              <button
                className="btn btn-success"
                onClick={() => setShowGenerateForm(true)}
              >
                <i className="fas fa-calendar-alt me-2"></i>
                Gerar Grade
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
              ></button>
            </div>
          )}

          {success && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess(null)}
              ></button>
            </div>
          )}



          {showGenerateForm && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-calendar-alt me-2"></i>
                  Gerar Nova Grade
                </h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleGenerateGrade}>
                   <div className="row">
                     {/* Coluna Esquerda - Nome da Grade e Período */}
                     <div className="col-md-6">
                       <div className="bg-light rounded-3 p-4 h-100">
                         <div className="d-flex align-items-center mb-4">
                           <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                             <i className="fas fa-graduation-cap text-white"></i>
                           </div>
                           <h5 className="mb-0 text-primary fw-bold">Informações da Grade</h5>
                         </div>
                         
                         <div className="mb-4">
                           <label htmlFor="generate-nome" className="form-label fw-semibold text-dark mb-2">
                             Nome da Grade *
                           </label>
                           <input
                             type="text"
                             className="form-control form-control-lg"
                             id="generate-nome"
                             value={generateFormData.nome}
                             onChange={(e) => setGenerateFormData({ ...generateFormData, nome: e.target.value })}
                             required
                             placeholder="Digite o nome da grade"
                             style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                           />
                         </div>
                         
                         <div className="mb-3">
                            <label className="form-label fw-semibold text-dark mb-3">Período *</label>
                            {loadingPeriodos ? (
                              <div className="d-flex justify-content-center py-3">
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                  <span className="visually-hidden">Carregando períodos...</span>
                                </div>
                              </div>
                            ) : (
                              <div className="row g-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {periodos.length === 0 ? (
                                  <div className="col-12">
                                    <div className="alert alert-warning mb-0">
                                      <i className="fas fa-exclamation-triangle me-2"></i>
                                      Nenhum período cadastrado. Cadastre um período primeiro.
                                    </div>
                                  </div>
                                ) : (
                                  periodos.map((periodo) => (
                                    <div key={periodo.id} className="col-12 mb-2">
                                      <div 
                                        className={`card h-100 cursor-pointer shadow-sm ${
                                          generateFormData.periodoId === periodo.id ? 'border-success bg-success text-white' : 'border-light bg-white text-dark'
                                        }`}
                                        onClick={() => setGenerateFormData({ ...generateFormData, periodoId: periodo.id! })}
                                        style={{ 
                                          cursor: 'pointer', 
                                          transition: 'all 0.3s ease',
                                          borderRadius: '12px',
                                          border: generateFormData.periodoId === periodo.id ? '2px solid #198754' : '2px solid #e9ecef',
                                          transform: generateFormData.periodoId === periodo.id ? 'scale(1.02)' : 'scale(1)'
                                        }}
                                      >
                                        <div className="card-body p-3">
                                          <div className="d-flex align-items-center">
                                            <div className={`rounded-circle d-inline-flex align-items-center justify-content-center me-3 ${
                                              generateFormData.periodoId === periodo.id ? 'bg-white text-success' : 'bg-light text-muted'
                                            }`} style={{ width: '40px', height: '40px' }}>
                                              <i className="fas fa-calendar-alt"></i>
                                            </div>
                                            <div className="flex-grow-1">
                                              <h6 className={`mb-1 fw-bold ${
                                                generateFormData.periodoId === periodo.id ? 'text-white' : 'text-dark'
                                              }`}>
                                                {periodo.nome}
                                              </h6>
                                              {generateFormData.periodoId === periodo.id && (
                                                <small className="text-white opacity-75">
                                                  <i className="fas fa-check-circle me-1"></i>
                                                  Selecionado
                                                </small>
                                              )}
                                            </div>
                                            {generateFormData.periodoId === periodo.id && (
                                              <div className="text-white">
                                                <i className="fas fa-check-circle fa-lg"></i>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                         </div>
                       </div>
                     </div>

                     {/* Coluna Direita - Configurações e Botões */}
                       <div className="col-md-6">
                         <div className="bg-light rounded-3 p-4 h-100">
                           <div className="d-flex align-items-center mb-4">
                             <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                               <i className="fas fa-cog text-white"></i>
                             </div>
                             <h5 className="mb-0 text-primary fw-bold">Configurações da Grade</h5>
                           </div>
                           
                           {loadingConfig ? (
                             <div className="text-center py-5">
                               <div className="spinner-border text-primary" role="status">
                                 <span className="visually-hidden">Carregando configurações...</span>
                               </div>
                               <p className="text-muted mt-3 mb-0">Carregando configurações...</p>
                             </div>
                           ) : (
                             <div className="row g-4">
                               {/* Coluna Esquerda - Aulas Geminadas */}
                               <div className="col-6">
                                 <div className="bg-white rounded-3 p-3 h-100 shadow-sm border">
                                   <div className="d-flex align-items-center mb-3">
                                     <i className="fas fa-link text-primary me-2"></i>
                                     <h6 className="mb-0 fw-semibold">Aulas Geminadas</h6>
                                   </div>
                                   
                                   <div className="mb-3">
                                     <label className="form-label text-muted small mb-2">Quantidade:</label>
                                     <div className="d-flex gap-2">
                                       {[1, 2].map(num => (
                                         <button
                                           key={num}
                                           type="button"
                                           onClick={() => handleConfigInputChange('aulaGeminada', num)}
                                           className={`btn flex-fill ${
                                             configuracao.aulaGeminada === num ? 'btn-primary' : 'btn-outline-primary'
                                           }`}
                                           style={{ borderRadius: '8px', fontWeight: '600' }}
                                         >
                                           {num}
                                         </button>
                                       ))}
                                     </div>
                                     <small className="text-muted d-block mt-2">
                                       Máximo de aulas geminadas
                                     </small>
                                   </div>

                                   <div className="border-top pt-3">
                                      <label className="form-label text-muted small mb-2">Obrigatória:</label>
                                      <div className="d-flex gap-2">
                                        <button
                                          type="button"
                                          onClick={() => handleConfigInputChange('aulaGeminadaObrigatoria', true)}
                                          className={`btn flex-fill ${
                                            configuracao.aulaGeminadaObrigatoria ? 'btn-primary' : 'btn-outline-primary'
                                          }`}
                                          style={{ borderRadius: '8px', fontWeight: '600' }}
                                        >
                                          Sim
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleConfigInputChange('aulaGeminadaObrigatoria', false)}
                                          className={`btn flex-fill ${
                                            !configuracao.aulaGeminadaObrigatoria ? 'btn-primary' : 'btn-outline-primary'
                                          }`}
                                          style={{ borderRadius: '8px', fontWeight: '600' }}
                                        >
                                          Não
                                        </button>
                                      </div>
                                      <small className="text-muted d-block mt-2">
                                        Força aulas a serem geminadas
                                      </small>
                                    </div>
                                 </div>
                               </div>

                               {/* Coluna Direita - Aulas por Período */}
                               <div className="col-6">
                                 <div className="bg-white rounded-3 p-3 h-100 shadow-sm border">
                                   <div className="d-flex align-items-center mb-3">
                                     <i className="fas fa-calendar-day text-success me-2"></i>
                                     <h6 className="mb-0 fw-semibold">Aulas por Dia</h6>
                                   </div>
                                   
                                   <div className="mb-3">
                                     <label className="form-label text-muted small mb-2">Máximo no período:</label>
                                     <div className="d-flex flex-column gap-2">
                                       {[1, 2, 3].map(num => (
                                         <button
                                           key={num}
                                           type="button"
                                           onClick={() => handleConfigInputChange('quantidadeAulasPeriodo', num)}
                                           className={`btn d-flex align-items-center justify-content-between ${
                                             configuracao.quantidadeAulasPeriodo === num ? 'btn-success' : 'btn-outline-success'
                                           }`}
                                           style={{ borderRadius: '8px', fontWeight: '600' }}
                                         >
                                           <span>{num} aula{num > 1 ? 's' : ''}</span>
                                           {configuracao.quantidadeAulasPeriodo === num && (
                                             <i className="fas fa-check"></i>
                                           )}
                                         </button>
                                       ))}
                                     </div>
                                     <small className="text-muted d-block mt-2">
                                       Limite diário por período
                                     </small>
                                   </div>
                                 </div>
                               </div>
                             </div>
                           )}
                           
                           {/* Botões de Ação */}
                           <div className="border-top mt-4 pt-4">
                             <div className="d-flex gap-3 justify-content-end">
                               <button
                                 type="button"
                                 className="btn btn-outline-secondary px-4"
                                 onClick={() => {
                                   setShowGenerateForm(false);
                                   setGenerateFormData({ nome: '', periodoId: '' });
                                 }}
                                 disabled={generatingGrade}
                                 style={{ borderRadius: '8px', fontWeight: '600' }}
                               >
                                 <i className="fas fa-times me-2"></i>
                                 Cancelar
                               </button>
                               <button 
                                 type="submit" 
                                 className="btn btn-success px-4" 
                                 disabled={generatingGrade}
                                 style={{ borderRadius: '8px', fontWeight: '600', boxShadow: '0 2px 4px rgba(40, 167, 69, 0.2)' }}
                               >
                                 {generatingGrade ? (
                                   <>
                                     <div className="spinner-border spinner-border-sm me-2" role="status">
                                       <span className="visually-hidden">Gerando...</span>
                                     </div>
                                     Gerando Grade...
                                   </>
                                 ) : (
                                   <>
                                     <i className="fas fa-magic me-2"></i>
                                     Gerar Grade
                                   </>
                                 )}
                               </button>
                             </div>
                           </div>
                         </div>
                       </div>
                   </div>

                </form>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <h5 className="mb-0">
                    <i className="fas fa-list me-2"></i>
                    Lista de Grades
                  </h5>
                </div>
                <div className="col-md-6">
                  <div className="input-group" style={{ width: '300px', marginLeft: 'auto' }}>
                    <span className="input-group-text">
                      <i className="fas fa-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar grades..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="mt-2">Carregando grades...</p>
                </div>
              ) : filteredGrades.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-table fa-3x text-muted mb-3"></i>
                  <p className="text-muted">
                    {searchTerm ? 'Nenhuma grade encontrada com o termo pesquisado.' : 'Nenhuma grade cadastrada ainda.'}
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th className="id-column">ID</th>
                        <th>Nome da Grade</th>
                        <th style={{width: '120px'}}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredGrades.map((grade) => (
                        <tr key={grade.id}>
                          <td className="id-column">{grade.id}</td>
                          <td 
                            onClick={() => handleViewGrade(grade)}
                            style={{ cursor: 'pointer' }}
                            title="Clique para visualizar a grade"
                          >
                            <strong>{grade.nome}</strong>
                            {grade.data && (
                              <div className="text-muted small">
                                Gerada em: {new Date(grade.data).toLocaleString('pt-BR')}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm" role="group">
                              <button
                                className="btn btn-outline-info"
                                onClick={() => handleViewGrade(grade)}
                                title="Visualizar Grade"
                              >
                                <i className="fas fa-eye"></i>
                              </button>

                              <button
                                className="btn btn-outline-danger"
                                onClick={() => grade.id && handleDelete(grade.id)}
                                title="Excluir"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {selectedGrade && selectedGrade.horarios && selectedGrade.horarios.length > 0 && (
            <div className="card mt-4">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-calendar-week me-2"></i>
                    Grade: {selectedGrade.nome}
                  </h5>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setSelectedGrade(null)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                {selectedGrade.data && (
                  <div className="text-muted small mt-1">
                    Gerada em: {new Date(selectedGrade.data).toLocaleString('pt-BR')}
                  </div>
                )}
              </div>
              <div className="card-body">
                {(() => {
                  const gradePorDia = organizarGradePorDia(selectedGrade.horarios!);
                  
                  return diasSemana.map(dia => {
                    const horariosNoDia = gradePorDia[dia.numero];
                    
                    if (horariosNoDia.length === 0) return null;
                    
                    const horariosUnicos = obterHorariosUnicos(horariosNoDia);
                    const turmasUnicas = obterTurmasUnicas(horariosNoDia);
                    
                    return (
                      <div key={dia.numero} className="mb-4">
                        <h6 className="text-primary border-bottom pb-2">
                          <i className="fas fa-calendar-day me-2"></i>
                          {dia.nome}
                        </h6>
                        <div className="table-responsive">
                          <table className="table table-bordered table-sm">
                            <thead className="table-light">
                              <tr>
                                <th style={{width: '100px'}}>Horário</th>
                                {turmasUnicas.map(([turmaId, turmaNome]) => (
                                  <th key={turmaId} className="text-center">{turmaNome}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {horariosUnicos.map(horario => {
                                const [inicio, fim] = horario.split('-');
                                return (
                                  <tr key={horario}>
                                    <td className="fw-bold text-center align-middle">
                                      {inicio}<br/>
                                      <small className="text-muted">{fim}</small>
                                    </td>
                                    {turmasUnicas.map(([turmaId]) => {
                                      const horarioEncontrado = findHorarioForTurmaAndTime(horariosNoDia, turmaId, inicio, fim);
                                      return (
                                        <td key={turmaId} className="text-center align-middle">
                                          {horarioEncontrado ? (
                                            <div className="small">
                                              <div className="fw-bold text-primary">
                                                {horarioEncontrado.disciplina.nome}
                                              </div>
                                              <div className="text-muted">
                                                {horarioEncontrado.disciplina.professor.nome}
                                              </div>
                                            </div>
                                          ) : (
                                            <span className="text-muted">-</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Modal de Carregamento para Gerar Grade */}
    <LoadingModal
      isOpen={showGenerateModal}
      title={generateModalTitle}
      message={generateModalMessage}
      state={generateModalState}
      sugestoes={generateModalSugestoes}
      extraActions={generateModalState === 'success' && generatedGradeId ? (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setShowGenerateModal(false);
            navigate(`/grades/view/${generatedGradeId}`);
          }}
        >
          <i className="fas fa-eye me-2"></i>
          Ver Grade
        </button>
      ) : undefined}
      onClose={() => {
        setShowGenerateModal(false);
        setGenerateModalState('loading');
        setGenerateModalSugestoes([]);
        setGeneratedGradeId(null);
      }}
    />



    {/* Modal de Confirmação */}
    <DeleteConfirmationModal
      isOpen={deleteModal.isOpen}
      title={deleteModal.title}
      message={deleteModal.message}
      itemName={deleteModal.itemName}
      onConfirm={deleteModal.onConfirm}
      onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
      isLoading={deleteModal.isLoading}
    />
    
    <CargaHorariaValidationModal
      isOpen={isValidationModalOpen}
      onClose={() => setIsValidationModalOpen(false)}
      onProceedAnyway={() => {
        setIsValidationModalOpen(false);
        executarGeracao();
      }}
      turmasComErro={turmasComErro}
      professoresComErro={professoresComErro}
      cargaHorariaPeriodo={cargaHorariaPeriodo}
    />
    </div>
    </>
  );
};

export default Grades;